import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, Check } from 'lucide-react';

/**
 * GuidedTour - Interactive onboarding tour with spotlight and tooltips
 *
 * Features:
 * - Step-by-step guidance with visual spotlight
 * - Smooth transitions between steps
 * - Skip/dismiss functionality
 * - Progress indicator
 * - Responsive positioning
 */
function GuidedTour({ isOpen, onClose, onComplete, userId, showNotification }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightPosition, setSpotlightPosition] = useState(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const tooltipRef = useRef(null);

  // Tour steps configuration
  const steps = [
    {
      id: 'welcome',
      title: '👋 Welcome to VerbaGPT!',
      description: 'Let\'s take a quick tour to help you get started. This will only take a minute.',
      target: null, // No specific target, centered modal
      position: 'center'
    },
    {
      id: 'input',
      title: '💬 Ask Questions',
      description: 'Type your questions here in natural language. Try sample questions from demo database.',
      target: '[data-tour="chat-input"]',
      position: 'top',
      offset: { x: 0, y: -100 }
    },
    {
      id: 'datasource',
      title: '🗄️ Active Datasource',
      description: 'Your active database is shown here. You\'re currently using our demo "Benchmarking" database.',
      target: '[data-tour="active-datasource"]',
      position: 'bottom',
      offset: { x: 0, y: 20 }
    },
    {
      id: 'add-datasource',
      title: '➕ Add Your Own Data',
      description: 'Click here to connect your own database (PostgreSQL, MySQL, MSSQL, Azure MSSQL, etc.)',
      target: '[data-tour="add-datasource"]',
      position: 'right',
      offset: { x: 20, y: 0 }
    },
    {
      id: 'data-notes',
      title: '📝 Data Notes',
      description: 'Add descriptions and context about your data. This helps VerbaGPT understand your database better.',
      target: '[data-tour="data-notes"]',
      position: 'right',
      offset: { x: 20, y: 0 }
    },
    {
      id: 'data-preview',
      title: '📊 Preview Your Data',
      description: 'Explore your database schema, view tables, and preview sample data here.',
      target: '[data-tour="data-preview"]',
      position: 'right',
      offset: { x: 20, y: 0 }
    },
    {
      id: 'mode-switcher',
      title: '🚀 Virgo & Taurus Modes',
      description: 'Use Virgo for cloud database analysis, and the powerful Taurus for everything including working with local files.',
      target: '[data-tour="mode-switcher"]',
      position: 'right',
      offset: { x: 20, y: 0 }
    },
    {
      id: 'snippets',
      title: '⚡ One-Click Context',
      description: 'Save reusable context snippets (footnotes) that you can quickly add to any query with one click.',
      target: '[data-tour="snippets"]',
      position: 'top',
      offset: { x: 0, y: -200 }
    },
    {
      id: 'complete',
      title: '🎉 You\'re All Set!',
      description: 'You\'re ready to start analyzing your data. Try asking a question to get started!',
      target: null,
      position: 'center'
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  // Calculate spotlight position based on target element
  useEffect(() => {
    if (!isOpen || !currentStepData.target) {
      setSpotlightPosition(null);
      return;
    }

    const updatePosition = () => {
      const targetElement = document.querySelector(currentStepData.target);
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        setSpotlightPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
        });
      } else {
        setSpotlightPosition(null);
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [isOpen, currentStepData.target, currentStep]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'ArrowRight' && !isLastStep) {
        handleNext();
      } else if (e.key === 'ArrowLeft' && !isFirstStep) {
        handlePrevious();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep, isLastStep, isFirstStep]);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    if (dontShowAgain && userId) {
      localStorage.setItem(`guided_tour_dismissed_${userId}`, 'true');
    }
    onClose();
  };

  const handleComplete = () => {
    if (userId) {
      if (dontShowAgain) {
        localStorage.setItem(`guided_tour_dismissed_${userId}`, 'true');
      }
      localStorage.setItem(`guided_tour_completed_${userId}`, 'true');
    }
    onComplete();
    onClose();
  };

  const getTooltipPosition = () => {
    if (currentStepData.position === 'center' || !spotlightPosition) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }

    const { top, left, width, height } = spotlightPosition;
    const offset = currentStepData.offset || { x: 0, y: 0 };

    switch (currentStepData.position) {
      case 'top':
        return {
          position: 'fixed',
          top: `${top - 160 + offset.y}px`,
          left: `${left + width / 2}px`,
          transform: 'translateX(-50%)'
        };
      case 'bottom':
        return {
          position: 'fixed',
          top: `${top + height + 20 + offset.y}px`,
          left: `${left + width / 2}px`,
          transform: 'translateX(-50%)'
        };
      case 'left':
        return {
          position: 'fixed',
          top: `${top + height / 2}px`,
          left: `${left - 320 + offset.x}px`,
          transform: 'translateY(-50%)'
        };
      case 'right':
        return {
          position: 'fixed',
          top: `${top + height / 2}px`,
          left: `${left + width + 20 + offset.x}px`,
          transform: 'translateY(-50%)'
        };
      default:
        return {};
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay with spotlight cutout */}
      <div className="fixed inset-0 z-[9999] pointer-events-none">
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/60 transition-opacity duration-300" />

        {/* Spotlight (transparent area) */}
        {spotlightPosition && (
          <div
            className="absolute bg-transparent ring-4 ring-white/30 rounded-lg transition-all duration-300 ease-out pointer-events-auto"
            style={{
              top: `${spotlightPosition.top - 8}px`,
              left: `${spotlightPosition.left - 8}px`,
              width: `${spotlightPosition.width + 16}px`,
              height: `${spotlightPosition.height + 16}px`,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)'
            }}
          />
        )}
      </div>

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-[10000] pointer-events-auto"
        style={getTooltipPosition()}
      >
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border-2 border-blue-500 dark:border-blue-400 max-w-sm w-full animate-in fade-in zoom-in-95 duration-300">
          {/* Header */}
          <div className="flex items-start justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white pr-8">
              {currentStepData.title}
            </h3>
            <button
              onClick={handleSkip}
              className="flex-shrink-0 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <X className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              {currentStepData.description}
            </p>

            {/* Progress indicator */}
            <div className="flex items-center gap-1 mb-4">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                    index === currentStep
                      ? 'bg-blue-600 dark:bg-blue-400'
                      : index < currentStep
                      ? 'bg-blue-400 dark:bg-blue-600'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Step {currentStep + 1} of {steps.length}
              </div>

              <div className="flex items-center gap-2">
                {!isFirstStep && (
                  <button
                    onClick={handlePrevious}
                    className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </button>
                )}

                {isLastStep ? (
                  <button
                    onClick={handleComplete}
                    className="px-4 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-1.5 font-medium"
                  >
                    <Check className="h-4 w-4" />
                    Get Started
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-1.5 font-medium"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Don't show again checkbox */}
          <div className="px-4 pb-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setDontShowAgain(isChecked);

                  if (isChecked && userId) {
                    // Save immediately and close
                    localStorage.setItem(`guided_tour_dismissed_${userId}`, 'true');
                    if (showNotification) {
                      showNotification('success', 'Tour disabled - you can restart it anytime from Settings');
                    }
                    onClose();
                  }
                }}
                className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-xs text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-300 transition-colors select-none">
                Don't show this tour again
              </span>
            </label>
          </div>
        </div>

        {/* Arrow pointer (for non-centered tooltips) */}
        {spotlightPosition && currentStepData.position !== 'center' && (
          <div
            className={`absolute w-0 h-0 border-8 ${
              currentStepData.position === 'bottom'
                ? 'border-t-0 border-b-white dark:border-b-gray-800 border-x-transparent -top-2 left-1/2 -translate-x-1/2'
                : currentStepData.position === 'top'
                ? 'border-b-0 border-t-white dark:border-t-gray-800 border-x-transparent -bottom-2 left-1/2 -translate-x-1/2'
                : currentStepData.position === 'right'
                ? 'border-l-0 border-r-white dark:border-r-gray-800 border-y-transparent -left-2 top-1/2 -translate-y-1/2'
                : 'border-r-0 border-l-white dark:border-l-gray-800 border-y-transparent -right-2 top-1/2 -translate-y-1/2'
            }`}
          />
        )}
      </div>
    </>
  );
}

export default GuidedTour;
